import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { get, postGet } from "../api/apiShared.ts";
import { markManualWrite } from "./manualQueryWrites.ts";

export interface DotLegalReactQueryOptions {
    enabled?: boolean;
    staleTime?: number;
    cacheTime?: number;
    refetchOnWindowFocus?: boolean;
    notifyOnChangePropsExclusions?: string[];
    refetchInterval?: number | false | ((query: any) => number | false);
    // Pass `keepPreviousData` (from @tanstack/react-query) to retain the prior result while a new query with a
    // changed key is fetching, so dependent UI (e.g. summary cards) doesn't flash empty/zero between fetches.
    placeholderData?: (previousData: any, previousQuery?: any) => any;
    // Queries throw into the nearest error boundary by default (throwOnError is set globally), which takes the
    // whole page down. Set false for a query whose result is an optional extra - a list of selectable values,
    // say - so losing it degrades one control instead of the page that hosts it.
    throwOnError?: boolean;
    retry?: boolean | number;
}

export interface DotLegalReactQueryResult<T> {
    data: T | undefined;
    isLoading: boolean;
    isIdle: boolean;
    isFetching: boolean;
    isPlaceholderData: boolean;
    isSuccess: boolean;
    error: unknown;
    refetch: () => Promise<void>;
}

export type DotLegalQueryKey = readonly unknown[];

export type DotLegalUpdater<T> = T | ((oldData: T | undefined) => T);

export interface DotLegalQuery {
    queryKey: DotLegalQueryKey;
    state: {
        status: "idle" | "loading" | "success" | "error";
    };
}

export interface DotLegalMutationOptions<TData, TVariables> {
    onMutate?: (variables: TVariables) => Promise<unknown> | unknown;
    onSuccess?: (data: TData, variables: TVariables) => void;
    onSettled?: (data: TData | undefined, error: unknown | null, variables: TVariables) => void;
}

export interface DotLegalMutationResult<TData, TVariables> {
    mutate: (variables: TVariables, options?: DotLegalMutationOptions<TData, TVariables>) => void;
    mutateAsync: (variables: TVariables, options?: DotLegalMutationOptions<TData, TVariables>) => Promise<TData>;
    isLoading: boolean;
    isIdle: boolean;
    data: TData | undefined;
    variables: TVariables | undefined;
    reset: () => void;
}

export function useDotLegalQuery<T>(url: string, options?: DotLegalReactQueryOptions): DotLegalReactQueryResult<T> {
    return useDotLegalQueryArray([url], url, options);
}

export function useDotLegalQueryWithKey<T>(queryKey: string, url: string, options?: DotLegalReactQueryOptions): DotLegalReactQueryResult<T> {
    return useDotLegalQueryArray([queryKey], url, options);
}

export function useDotLegalQueryArray<T>(key: Array<any>, url: string, options?: DotLegalReactQueryOptions): DotLegalReactQueryResult<T> {
    const { cacheTime, ...restOptions } = options ?? {};
    let result = useQuery({ queryKey: key, queryFn: () => get<T>(url), gcTime: cacheTime, ...restOptions });

    return toDotLegalResult(result);
}

export function useDotLegalPostGetQuery<T>(url: string, body: any, options?: DotLegalReactQueryOptions): DotLegalReactQueryResult<T> {
    return useDotLegalPostGetQueryArray([url], url, body, options);
}

export function useDotLegalPostGetQueryWithKey<T>(key: string, url: string, body: any, options?: DotLegalReactQueryOptions): DotLegalReactQueryResult<T> {
    return useDotLegalPostGetQueryArray([key], url, body, options);
}

export function useDotLegalPostGetQueryArray<T>(key: Array<any>, url: string, body: any, options?: DotLegalReactQueryOptions): DotLegalReactQueryResult<T> {
    const { cacheTime, ...restOptions } = options ?? {};
    let result = useQuery({ queryKey: key, queryFn: () => postGet<T>(url, body), gcTime: cacheTime, ...restOptions });

    return toDotLegalResult(result);
}

// React Query only re-renders a component for the fields it read during render, and it learns which by watching
// property reads on the result it returns. Spreading that result read every field, so every consumer re-rendered
// on every change of every field - 50 table rows repainting because a card beside them refetched a count they
// never look at. The fields that flip on a refetch without the data changing are read lazily, so only a consumer
// that uses them tracks them. The rest are read here, so a change in them keeps re-rendering every consumer as
// before; data in particular has to stay a plain property, because one hook assigns to it.
function toDotLegalResult<T>(result: UseQueryResult<T>): DotLegalReactQueryResult<T> {
    return {
        data: result.data,
        error: result.error,
        isLoading: result.isLoading,
        isSuccess: result.isSuccess,
        isPlaceholderData: result.isPlaceholderData,
        refetch: result.refetch as unknown as () => Promise<void>,
        get isFetching() {
            return result.isFetching;
        },
        get isIdle() {
            return result.fetchStatus === "idle" && result.status === "pending";
        },
    };
}

export function useDotLegalMutation<TData, TVariables = void>(
    mutationFn: (variables: TVariables) => Promise<TData>,
    options?: DotLegalMutationOptions<TData, TVariables>
): DotLegalMutationResult<TData, TVariables> {
    const mutation = useMutation({ mutationFn: mutationFn, onMutate: options?.onMutate, onSuccess: options?.onSuccess, onSettled: options?.onSettled });

    return {
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
        isLoading: mutation.isPending,
        isIdle: mutation.isIdle,
        data: mutation.data,
        variables: mutation.variables,
        reset: mutation.reset,
    };
}

function resolveUpdater<T>(updater: DotLegalUpdater<T>, oldData: T | undefined): T {
    return typeof updater === "function" ? (updater as (oldData: T | undefined) => T)(oldData) : updater;
}

export function useDotLegalQueryClient() {
    const queryClient = useQueryClient();

    function setQueryData<T>(queryKey: string, updater: DotLegalUpdater<T>) {
        setQueryDataArray([queryKey], updater);
    }

    function setQueryDataArray<T>(queryKey: Array<unknown>, updater: DotLegalUpdater<T>) {
        queryClient.setQueryData<T>(queryKey, (oldData) => markManualWrite(resolveUpdater(updater, oldData)));
    }

    function getQueryData<T>(queryKey: string): T | undefined {
        return queryClient.getQueryData<T>([queryKey]);
    }

    async function refetchQueries(queryKey: string) {
        return queryClient.refetchQueries({ queryKey: [queryKey] });
    }

    async function refetchQueriesByPredicate(predicate: (query: DotLegalQuery) => boolean) {
        return queryClient.refetchQueries({ predicate: predicate as any, type: "all" });
    }

    async function cancelQueries(queryKey: string) {
        return queryClient.cancelQueries({ queryKey: [queryKey] });
    }

    async function cancelQueriesByPredicate(predicate: (query: DotLegalQuery) => boolean) {
        return queryClient.cancelQueries({ predicate: predicate as any });
    }

    function clear() {
        queryClient.clear();
    }

    async function fetchQuery<T>(queryKey: string, queryFn: () => Promise<T>): Promise<T> {
        return queryClient.fetchQuery({ queryKey: [queryKey], queryFn });
    }

    return {
        setQueryData,
        setQueryDataArray,
        getQueryData,
        refetchQueries,
        refetchQueriesByPredicate,
        cancelQueries,
        cancelQueriesByPredicate,
        clear,
        fetchQuery,
        getQueryCache: queryClient.getQueryCache,
    };
}
